/**
 * Statistics Export Utility
 * Exports all statistics data to Excel (.xlsx) format
 */

import * as XLSX from 'xlsx';
import {
  BackendTradeStatistics,
  SymbolStatisticsResponse,
  RiskMetricsResponse,
  TimeAnalysisResponse,
  TradeStatisticsFilters,
  PnlOverTimeResponse,
  DistributionResponse,
  MonthlyPerformanceResponse,
  WeeklyBreakdownResponse,
  DailyActivityResponse,
  ComparativeAnalysisResponse,
  SessionPerformanceResponse,
} from '../types/trade';
import {
  PortfolioGrowthResponse,
  WinRateTrendResponse,
} from '../services/tradeStatisticsApi';
import { formatDisplayCurrency } from './formatDisplayCurrency';
import { getSyncedDisplayCurrency } from './displayCurrencySync';

/**
 * Format percentage value
 */
const formatPercent = (value: number | null | undefined): string => {
  if (value === null || value === undefined || isNaN(value)) return 'N/A';
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
};

/**
 * Format number value
 */
const formatNumber = (value: number | null | undefined, decimals: number = 0): string => {
  if (value === null || value === undefined || isNaN(value)) return 'N/A';
  return value.toFixed(decimals);
};

const formatHours = (value: number | null | undefined): string => {
  if (value === null || value === undefined || isNaN(value)) return 'N/A';
  return `${value.toFixed(2)} hrs`;
};

/**
 * Auto-size columns in a worksheet
 */
const autoSizeColumns = (worksheet: XLSX.WorkSheet) => {
  const colWidths: number[] = [];
  const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
  
  // Find max width for each column
  for (let C = range.s.c; C <= range.e.c; ++C) {
    let maxWidth = 10; // Minimum width
    for (let R = range.s.r; R <= range.e.r; ++R) {
      const cellAddress = XLSX.utils.encode_cell({ c: C, r: R });
      const cell = worksheet[cellAddress];
      if (cell && cell.v) {
        const cellValue = String(cell.v);
        // Estimate width: count characters, add padding for formatting
        const estimatedWidth = cellValue.length + 2;
        if (estimatedWidth > maxWidth) {
          maxWidth = estimatedWidth;
        }
      }
    }
    // Cap at reasonable maximum (50 characters)
    colWidths[C] = Math.min(maxWidth, 50);
  }
  
  worksheet['!cols'] = colWidths.map((w) => ({ wch: w }));
};

/**
 * Export statistics data to Excel
 */
export const exportStatisticsToExcel = async (data: {
  // Main statistics
  statistics: BackendTradeStatistics | null;
  
  // Symbol statistics
  symbolStats: SymbolStatisticsResponse | null;
  
  // Risk metrics
  riskMetrics: RiskMetricsResponse | null;
  
  // Time analysis
  timeAnalysis: TimeAnalysisResponse | null;
  
  // Chart data
  portfolioGrowth?: PortfolioGrowthResponse | null;
  winRateTrend?: WinRateTrendResponse | null;
  pnlOverTime?: PnlOverTimeResponse | null;
  distribution?: DistributionResponse | null;
  monthlyPerformance?: MonthlyPerformanceResponse | null;
  weeklyBreakdown?: WeeklyBreakdownResponse | null;
  dailyActivity?: DailyActivityResponse | null;
  comparativeAnalysis?: ComparativeAnalysisResponse | null;
  sessionPerformance?: SessionPerformanceResponse | null;
  
  // Filters for metadata
  filters?: TradeStatisticsFilters;
  
  // Timeframe info
  timeframe?: string;
  dateRange?: { startDate: Date | null; endDate: Date | null };

  // Comparison period (when comparison range is selected)
  comparisonStatistics?: BackendTradeStatistics | null;
  comparisonDateRange?: { startDate: Date | null; endDate: Date | null };
  comparisonSymbolStats?: SymbolStatisticsResponse | null;
  comparisonRiskMetrics?: RiskMetricsResponse | null;
  comparisonTimeAnalysis?: TimeAnalysisResponse | null;
  comparisonSessionPerformance?: SessionPerformanceResponse | null;
  comparisonMonthlyPerformance?: MonthlyPerformanceResponse | null;
  comparisonWeeklyBreakdown?: WeeklyBreakdownResponse | null;
  comparisonDailyActivity?: DailyActivityResponse | null;
  comparisonComparativeAnalysis?: ComparativeAnalysisResponse | null;

  /** In-app display currency (falls back to synced user preference). */
  displayCurrency?: string;
}) => {
  const ccy = (data.displayCurrency || getSyncedDisplayCurrency()).toUpperCase();
  const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    return formatDisplayCurrency(value, ccy, { locale: 'en-US' });
  };
  const hasComparison = Boolean(
    data.comparisonDateRange?.startDate &&
    data.comparisonDateRange?.endDate
  );
  const comp = data.comparisonStatistics ?? null;
  const compDate = data.comparisonDateRange;
  const workbook = XLSX.utils.book_new();

  // 0. Summary/Executive Sheet (created first, will appear first)
  if (data.statistics) {
    const summaryData: any[][] = [
      ['Statistics Export Summary'],
      [],
      ['Export Information'],
      ['Export Date', new Date().toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })],
      [],
      ['Date Range'],
    ];

    if (data.dateRange?.startDate && data.dateRange?.endDate) {
      summaryData.push([
        'Selected Period',
        `${data.dateRange.startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${data.dateRange.endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`,
      ]);
    } else if (data.timeframe) {
      summaryData.push(['Timeframe', data.timeframe]);
    } else {
      summaryData.push(['Timeframe', 'All Time']);
    }

    if (hasComparison && compDate?.startDate && compDate?.endDate) {
      summaryData.push([
        'Comparison Period',
        `${compDate.startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${compDate.endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`,
      ]);
    }

    summaryData.push(
      [],
      ['Key Performance Metrics'],
      ['Metric', 'Value'],
      ['Total Trades', data.statistics.total_trades],
      ['Closed Trades', data.statistics.closed_trades],
      ['Winning Trades', data.statistics.winning_trades],
      ['Losing Trades', data.statistics.losing_trades],
      ['Win Rate', formatPercent(data.statistics.win_rate)],
      ['Total P&L', formatCurrency(data.statistics.total_pnl)],
      ['Average Win', formatCurrency(data.statistics.average_win)],
      ['Average Loss', formatCurrency(data.statistics.average_loss)],
      ['Largest Win', formatCurrency(data.statistics.largest_win)],
      ['Largest Loss', formatCurrency(data.statistics.largest_loss)],
      ['Total Return %', formatPercent(data.statistics.total_return_percent)],
      ['Portfolio Value', formatCurrency(data.statistics.portfolio_value)],
      [],
      ['Risk Metrics'],
      ['Metric', 'Value'],
    );

    if (data.riskMetrics) {
      summaryData.push(
        ['Maximum Drawdown', formatCurrency(data.riskMetrics.max_drawdown)],
        ['Max Drawdown %', formatPercent(data.riskMetrics.max_drawdown_percent)],
        ['Sharpe Ratio', formatNumber(data.riskMetrics.sharpe_ratio, 2)],
        ['Sortino Ratio', formatNumber(data.riskMetrics.sortino_ratio, 2)],
        ['Profit Factor', data.statistics.profit_factor ? formatNumber(data.statistics.profit_factor, 2) : 'N/A'],
      );
    } else {
      summaryData.push(['Risk metrics not available']);
    }

    if (hasComparison && comp) {
      const pctChg = (sel: number, cmp: number) => {
        if (!Number.isFinite(sel) || !Number.isFinite(cmp)) return 'N/A';
        if (sel === 0) return cmp === 0 ? '0%' : 'N/A';
        return formatPercent(((cmp - sel) / Math.abs(sel)) * 100);
      };
      summaryData.push(
        [],
        ['Comparison Period – Key Metrics'],
        ['Metric', 'Selected', 'Comparison', '% Change'],
        ['Total Trades', data.statistics.total_trades, comp.total_trades, pctChg(data.statistics.total_trades, comp.total_trades)],
        ['Winning Trades', data.statistics.winning_trades, comp.winning_trades, pctChg(data.statistics.winning_trades, comp.winning_trades)],
        ['Losing Trades', data.statistics.losing_trades, comp.losing_trades, pctChg(data.statistics.losing_trades, comp.losing_trades)],
        ['Win Rate', formatPercent(data.statistics.win_rate), formatPercent(comp.win_rate), pctChg(data.statistics.win_rate, comp.win_rate)],
        ['Total P&L', formatCurrency(data.statistics.total_pnl), formatCurrency(comp.total_pnl), pctChg(data.statistics.total_pnl ?? 0, comp.total_pnl ?? 0)],
        ['Average Win', formatCurrency(data.statistics.average_win), formatCurrency(comp.average_win), '—'],
        ['Average Loss', formatCurrency(data.statistics.average_loss), formatCurrency(comp.average_loss), '—'],
        ['Largest Win', formatCurrency(data.statistics.largest_win), formatCurrency(comp.largest_win), '—'],
        ['Largest Loss', formatCurrency(data.statistics.largest_loss), formatCurrency(comp.largest_loss), '—'],
      );
    }

    summaryData.push(
      [],
      ['Performance Summary'],
      ['Metric', 'Value'],
    );

    if (data.symbolStats?.symbols && data.symbolStats.symbols.length > 0) {
      summaryData.push(['Total Symbols Traded', data.symbolStats.symbols.length]);
      const topSymbol = data.symbolStats.symbols
        .sort((a, b) => b.total_pnl - a.total_pnl)[0];
      if (topSymbol) {
        summaryData.push(
          ['Best Performing Symbol', `${topSymbol.symbol} (${formatCurrency(topSymbol.total_pnl)})`],
        );
      }
    }

    if (data.statistics.most_traded_symbol) {
      summaryData.push([
        'Most Traded Symbol',
        `${data.statistics.most_traded_symbol} (${data.statistics.most_traded_symbol_count} trades)`,
      ]);
    }

    summaryData.push(
      [],
      ['Streak Analysis'],
      ['Metric', 'Value'],
      ['Current Win Streak', data.statistics.current_win_streak],
      ['Current Loss Streak', data.statistics.current_loss_streak],
      ['Longest Win Streak', data.statistics.longest_win_streak],
      ['Longest Loss Streak', data.statistics.longest_loss_streak],
    );

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    autoSizeColumns(summarySheet);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
  }

  // Generate filename with date range or timeframe
  let filename = 'statistics_export_';
  
  if (data.dateRange?.startDate && data.dateRange?.endDate) {
    // Custom date range selected - use the date range in filename
    const startDateStr = data.dateRange.startDate.toISOString().split('T')[0].replace(/-/g, '');
    const endDateStr = data.dateRange.endDate.toISOString().split('T')[0].replace(/-/g, '');
    filename += `${startDateStr}_to_${endDateStr}`;
  } else if (data.dateRange?.startDate) {
    // Only start date selected
    const startDateStr = data.dateRange.startDate.toISOString().split('T')[0].replace(/-/g, '');
    filename += `from_${startDateStr}`;
  } else if (data.dateRange?.endDate) {
    // Only end date selected
    const endDateStr = data.dateRange.endDate.toISOString().split('T')[0].replace(/-/g, '');
    filename += `until_${endDateStr}`;
  } else {
    // Use timeframe if no custom dates
    const timeframeStr = data.timeframe || 'all-time';
    filename += timeframeStr;
  }
  
  // Add export timestamp
  const exportTimestamp = new Date().toISOString().split('T')[0].replace(/-/g, '');
  filename += `_${exportTimestamp}.xlsx`;

  // 1. Overview Sheet
  if (data.statistics) {
    const overviewData: any[][] = [
      ['Overview Statistics'],
      [],
      ['Metric', 'Value'],
      ['Total Trades', data.statistics.total_trades],
      ['Open Trades', data.statistics.open_trades],
      ['Closed Trades', data.statistics.closed_trades],
      ['Winning Trades', data.statistics.winning_trades],
      ['Losing Trades', data.statistics.losing_trades],
      ['Win Rate', formatPercent(data.statistics.win_rate)],
      ['Total P&L', formatCurrency(data.statistics.total_pnl)],
      ['Average Win', formatCurrency(data.statistics.average_win)],
      ['Average Loss', formatCurrency(data.statistics.average_loss)],
      ['Largest Win', formatCurrency(data.statistics.largest_win)],
      ['Largest Loss', formatCurrency(data.statistics.largest_loss)],
      ['Total Return %', formatPercent(data.statistics.total_return_percent)],
      ['Average Return %', formatPercent(data.statistics.average_return_percent)],
      ['Portfolio Value', formatCurrency(data.statistics.portfolio_value)],
      ['Portfolio Change %', formatPercent(data.statistics.portfolio_change_percent)],
      ['Win Rate Change %', formatPercent(data.statistics.win_rate_change_percent)],
      [],
      ['Portfolio Growth Data'],
      ['Date', 'Portfolio Value', 'Opening Balance', 'Cumulative P&L', 'Cumulative Transfers', 'Daily P&L', 'Daily Transfers', 'Trade Count'],
    ];

    // Add portfolio growth data points
    if (data.portfolioGrowth?.data_points && data.portfolioGrowth.data_points.length > 0) {
      data.portfolioGrowth.data_points.forEach((point) => {
        overviewData.push([
          point.date,
          point.portfolio_value,
          point.opening_balance || '',
          point.cumulative_pnl,
          point.cumulative_transfers,
          point.daily_pnl,
          point.daily_transfers,
          point.trade_count,
        ]);
      });
    } else {
      overviewData.push(['No portfolio growth data available']);
    }

    overviewData.push([], ['Win Rate Trend Data']);
    overviewData.push(['Date', 'Cumulative Win Rate', 'Cumulative Wins', 'Cumulative Total', 'Period Wins', 'Period Total', 'Period Win Rate']);

    // Add win rate trend data points
    if (data.winRateTrend?.data_points && data.winRateTrend.data_points.length > 0) {
      data.winRateTrend.data_points.forEach((point) => {
        overviewData.push([
          point.date,
          formatPercent(point.cumulative_win_rate),
          point.cumulative_wins,
          point.cumulative_total,
          point.period_wins,
          point.period_total,
          formatPercent(point.period_win_rate),
        ]);
      });
    } else {
      overviewData.push(['No win rate trend data available']);
    }

    overviewData.push([], ['P&L Over Time']);
    if (data.pnlOverTime?.data_points && data.pnlOverTime.data_points.length > 0) {
      overviewData.push(['Date', 'Total P&L', 'Trade Count', 'Winning Trades', 'Losing Trades']);
      data.pnlOverTime.data_points.forEach((point) => {
        overviewData.push([
          point.date,
          formatCurrency(point.total_pnl),
          point.trade_count,
          point.winning_trades,
          point.losing_trades,
        ]);
      });
    } else {
      overviewData.push(['No P&L over time data available']);
    }

    overviewData.push([], ['Trade Distribution by Symbol']);
    if (data.distribution?.by_symbol && data.distribution.by_symbol.length > 0) {
      overviewData.push(['Symbol', 'Trade Count']);
      data.distribution.by_symbol.forEach((item) => {
        overviewData.push([
          item.symbol,
          item.count,
        ]);
      });
    } else {
      overviewData.push(['No symbol distribution data available']);
    }

    overviewData.push([], ['Trade Distribution by Type']);
    if (data.distribution?.long_short) {
      overviewData.push(['Type', 'Trade Count']);
      overviewData.push(['Long', data.distribution.long_short.long_trades]);
      overviewData.push(['Short', data.distribution.long_short.short_trades]);
    }

    overviewData.push([], ['Monthly Performance']);
    if (data.monthlyPerformance?.data_points && data.monthlyPerformance.data_points.length > 0) {
      overviewData.push(['Month', 'Trades', 'Wins', 'Losses', 'Total P&L', 'Win Rate', 'Avg Win', 'Avg Loss', 'Largest Win', 'Largest Loss']);
      data.monthlyPerformance.data_points.forEach((month) => {
        overviewData.push([
          month.month,
          month.total_trades,
          month.winning_trades,
          month.losing_trades,
          formatCurrency(month.total_pnl),
          formatPercent(month.win_rate),
          formatCurrency(month.average_win),
          formatCurrency(month.average_loss),
          formatCurrency(month.largest_win),
          formatCurrency(month.largest_loss),
        ]);
      });
    } else {
      overviewData.push(['No monthly performance data available']);
    }

    overviewData.push([], ['Weekly Breakdown']);
    if (data.weeklyBreakdown?.data_points && data.weeklyBreakdown.data_points.length > 0) {
      overviewData.push(['Week Start', 'Trades', 'Wins', 'Losses', 'Total P&L', 'Win Rate', 'Avg Win', 'Avg Loss', 'Largest Win', 'Largest Loss']);
      data.weeklyBreakdown.data_points.forEach((week) => {
        overviewData.push([
          week.week_start,
          week.total_trades,
          week.winning_trades,
          week.losing_trades,
          formatCurrency(week.total_pnl),
          formatPercent(week.win_rate),
          formatCurrency(week.average_win),
          formatCurrency(week.average_loss),
          formatCurrency(week.largest_win),
          formatCurrency(week.largest_loss),
        ]);
      });
    } else {
      overviewData.push(['No weekly breakdown data available']);
    }

    overviewData.push([], ['Daily Activity']);
    if (data.dailyActivity?.daily_data_points && data.dailyActivity.daily_data_points.length > 0) {
      overviewData.push(['Date', 'Trades', 'Wins', 'Losses', 'Total P&L', 'Win Rate']);
      data.dailyActivity.daily_data_points.forEach((day) => {
        overviewData.push([
          day.date,
          day.trade_count,
          day.winning_trades || 0,
          day.losing_trades || 0,
          formatCurrency(day.total_pnl),
          formatPercent(day.win_rate),
        ]);
      });
    } else {
      overviewData.push(['No daily activity data available']);
    }

    overviewData.push([], ['Comparative Analysis']);
    if (data.comparativeAnalysis) {
      const comp = data.comparativeAnalysis;
      
      // Win/Loss Analysis
      overviewData.push(['Win/Loss Analysis']);
      overviewData.push(['Metric', 'Value']);
      if (comp.win_loss) {
        overviewData.push(['Winning Trades', comp.win_loss.winning_trades]);
        overviewData.push(['Losing Trades', comp.win_loss.losing_trades]);
        overviewData.push(['Average Win', formatCurrency(comp.win_loss.average_win)]);
        overviewData.push(['Average Loss', formatCurrency(comp.win_loss.average_loss)]);
        overviewData.push(['Win/Loss Ratio', comp.win_loss.win_loss_ratio ? formatNumber(comp.win_loss.win_loss_ratio, 2) : 'N/A']);
      }
      
      // Long/Short Analysis
      overviewData.push([], ['Long/Short Analysis']);
      overviewData.push(['Metric', 'Value']);
      if (comp.long_short) {
        overviewData.push(['Long Trades', comp.long_short.long_trades]);
        overviewData.push(['Short Trades', comp.long_short.short_trades]);
        overviewData.push(['Average Long P&L', formatCurrency(comp.long_short.average_long_pnl)]);
        overviewData.push(['Average Short P&L', formatCurrency(comp.long_short.average_short_pnl)]);
        overviewData.push(['Winning Long Trades', comp.long_short.winning_long_trades]);
        overviewData.push(['Losing Long Trades', comp.long_short.losing_long_trades]);
        overviewData.push(['Winning Short Trades', comp.long_short.winning_short_trades]);
        overviewData.push(['Losing Short Trades', comp.long_short.losing_short_trades]);
        overviewData.push(['Long Win Rate', formatPercent(comp.long_short.long_win_rate)]);
        overviewData.push(['Short Win Rate', formatPercent(comp.long_short.short_win_rate)]);
      }
      
      // Distribution
      overviewData.push([], ['Distribution']);
      if (comp.distribution) {
        overviewData.push(['Distribution Type', 'Value']);
        if (comp.distribution.win_loss) {
          overviewData.push(['Winning Trades (Distribution)', comp.distribution.win_loss.winning_trades]);
          overviewData.push(['Losing Trades (Distribution)', comp.distribution.win_loss.losing_trades]);
          overviewData.push(['Scratch Trades', comp.distribution.win_loss.scratch_trades]);
        }
        if (comp.distribution.long_short) {
          overviewData.push(['Long Trades (Distribution)', comp.distribution.long_short.long_trades]);
          overviewData.push(['Short Trades (Distribution)', comp.distribution.long_short.short_trades]);
        }
        if (comp.distribution.by_symbol) {
          overviewData.push([], ['Distribution by Symbol']);
          overviewData.push(['Symbol', 'Trade Count']);
          comp.distribution.by_symbol.forEach((item) => {
            overviewData.push([item.symbol, item.count]);
          });
        }
      }
    }

    const overviewSheet = XLSX.utils.aoa_to_sheet(overviewData);
    autoSizeColumns(overviewSheet);
    XLSX.utils.book_append_sheet(workbook, overviewSheet, 'Overview');
  }

  // 2. Detailed Statistics Sheet
  if (data.statistics) {
    const detailedData: any[][] = [
      ['Detailed Statistics'],
      [],
      ['Metric', 'Value'],
      ['Total Gain/Loss', formatCurrency(data.statistics.total_pnl)],
      ['Average Daily Gain/Loss', formatCurrency(data.statistics.average_daily_gain_loss)],
      ['Average Trade Gain/Loss', formatCurrency(data.statistics.average_per_trade_pnl)],
      ['Total Number of Trades', data.statistics.total_trades],
      ['Average Hold Time (scratch trades)', formatNumber(data.statistics.average_hold_time_scratch, 2) + ' hrs'],
      ['Number of Scratch Trades', data.statistics.closed_trades - data.statistics.winning_trades - data.statistics.losing_trades],
      ['Trade P&L Standard Deviation', formatCurrency(data.statistics.trade_pnl_standard_deviation)],
      ['Kelly Percentage', formatPercent(data.statistics.kelly_percentage)],
      ['Largest Gain', formatCurrency(data.statistics.largest_win)],
      ['Average Daily Volume', formatNumber(data.statistics.average_daily_volume, 2)],
      ['Average Winning Trade', formatCurrency(data.statistics.average_win)],
      ['Number of Winning Trades', data.statistics.winning_trades],
      ['Average Hold Time (winning trades)', formatNumber(data.statistics.average_hold_time_winning, 2) + ' hrs'],
      ['Max Consecutive Wins', data.statistics.longest_win_streak],
      ['System Quality Number (SQN)', formatNumber(data.statistics.system_quality_number, 2)],
      ['Total Fees', formatCurrency(data.statistics.total_fees)],
      ['Largest Loss', formatCurrency(data.statistics.largest_loss)],
      ['Average Per-share Gain/Loss', formatCurrency(data.statistics.average_per_unit_pnl)],
      ['Average Losing Trade', formatCurrency(data.statistics.average_loss)],
      ['Number of Losing Trades', data.statistics.losing_trades],
      ['Average Hold Time (losing trades)', formatNumber(data.statistics.average_hold_time_losing, 2) + ' hrs'],
      ['Max Consecutive Losses', data.statistics.longest_loss_streak],
      ['Profit Factor', formatNumber(data.statistics.profit_factor, 2)],
      ['Total Commissions', formatCurrency(data.statistics.total_commissions)],
    ];

    const detailedSheet = XLSX.utils.aoa_to_sheet(detailedData);
    autoSizeColumns(detailedSheet);
    XLSX.utils.book_append_sheet(workbook, detailedSheet, 'Detailed');
  }

  // 2a. System Quality Sheet (matches Statistics tab table)
  if (data.statistics) {
    const systemQualityData: any[][] = [
      ['System Quality'],
      [],
      ['Metric', 'Value'],
      ['Profit Factor', formatNumber(data.statistics.profit_factor, 2)],
      ['Expectancy', formatCurrency(data.statistics.average_per_trade_pnl)],
      ['Kelly Percentage', formatPercent(data.statistics.kelly_percentage)],
      ['SQN', formatNumber(data.statistics.system_quality_number, 2)],
      ['P&L Std Deviation', formatCurrency(data.statistics.trade_pnl_standard_deviation)],
    ];

    const systemQualitySheet = XLSX.utils.aoa_to_sheet(systemQualityData);
    autoSizeColumns(systemQualitySheet);
    XLSX.utils.book_append_sheet(workbook, systemQualitySheet, 'System Quality');
  }

  // 2b. Averages & Returns Sheet (matches Statistics tab table)
  if (data.statistics) {
    const averagesReturnsData: any[][] = [
      ['Averages & Returns'],
      [],
      ['Metric', 'Value'],
      ['Average Win', formatCurrency(data.statistics.average_win)],
      ['Average Loss', formatCurrency(data.statistics.average_loss)],
      ['Avg Daily Gain/Loss', formatCurrency(data.statistics.average_daily_gain_loss)],
      ['Avg Per-share Gain/Loss', formatCurrency(data.statistics.average_per_unit_pnl)],
      ['Avg Daily Volume', formatNumber(data.statistics.average_daily_volume, 2)],
    ];

    const averagesReturnsSheet = XLSX.utils.aoa_to_sheet(averagesReturnsData);
    autoSizeColumns(averagesReturnsSheet);
    XLSX.utils.book_append_sheet(workbook, averagesReturnsSheet, 'Averages & Returns');
  }

  // 2c. Timing & Costs Sheet (matches Statistics tab table)
  if (data.statistics) {
    const timingCostsData: any[][] = [
      ['Timing & Costs'],
      [],
      ['Metric', 'Value'],
      ['Hold Time (Winners)', formatHours(data.statistics.average_hold_time_winning)],
      ['Hold Time (Losers)', formatHours(data.statistics.average_hold_time_losing)],
      ['Hold Time (Scratch)', formatHours(data.statistics.average_hold_time_scratch)],
      ['Total Fees', formatCurrency(data.statistics.total_fees)],
      ['Total Commissions', formatCurrency(data.statistics.total_commissions)],
    ];

    const timingCostsSheet = XLSX.utils.aoa_to_sheet(timingCostsData);
    autoSizeColumns(timingCostsSheet);
    XLSX.utils.book_append_sheet(workbook, timingCostsSheet, 'Timing & Costs');
  }

  // 3. Risk Metrics Sheet
  if (data.riskMetrics) {
    const riskData: any[][] = [
      ['Risk Metrics'],
      [],
      ['Metric', 'Value', 'Description'],
      ['Maximum Drawdown', formatCurrency(data.riskMetrics.max_drawdown), 'The largest peak-to-trough decline in portfolio value'],
      ['Max Drawdown %', formatPercent(data.riskMetrics.max_drawdown_percent), 'Maximum drawdown as a percentage of peak value'],
      ['Sharpe Ratio', formatNumber(data.riskMetrics.sharpe_ratio, 2), 'Risk-adjusted return metric. Higher is better. >1 is good, >2 is very good, >3 is excellent.'],
      ['Sortino Ratio', formatNumber(data.riskMetrics.sortino_ratio, 2), 'Similar to Sharpe but only penalizes downside volatility'],
      ['Risk/Reward Ratio', data.riskMetrics.risk_reward_ratio ? `${formatNumber(data.riskMetrics.risk_reward_ratio, 2)}:1` : 'N/A', 'Average win divided by average loss'],
      ['Calmar Ratio', formatNumber(data.riskMetrics.calmar_ratio, 2), 'Annual return divided by maximum drawdown. Only computed when the timeframe is longer than 1 week.'],
      ['Largest Losing Streak', data.riskMetrics.largest_losing_streak, 'Longest consecutive sequence of losing trades'],
      ['Average Losing Trade', formatCurrency(data.riskMetrics.average_losing_trade), 'Average loss per losing trade'],
      ['Volatility', formatPercent(data.riskMetrics.volatility), 'Standard deviation of returns'],
      [],
      ['Drawdown Over Time'],
      ['Date', 'Drawdown'],
    ];

    // Add drawdown data points
    if (data.riskMetrics.drawdown_data) {
      data.riskMetrics.drawdown_data.forEach((point) => {
        riskData.push([point.date, formatCurrency(point.drawdown)]);
      });
    }

    const riskSheet = XLSX.utils.aoa_to_sheet(riskData);
    autoSizeColumns(riskSheet);
    XLSX.utils.book_append_sheet(workbook, riskSheet, 'Risk Metrics');
  }

  // 4. Time Analysis Sheet
  if (data.timeAnalysis) {
    const timeData: any[][] = [
      ['Time Analysis'],
      [],
      ['Performance by Hour of Day'],
      ['Hour', 'Trades', 'Total P&L', 'Win Rate', 'Wins', 'Losses'],
    ];

    // Add hourly performance
    if (data.timeAnalysis.by_hour) {
      data.timeAnalysis.by_hour.forEach((hour) => {
        if (hour.trade_count > 0) {
          timeData.push([
            `${hour.hour}:00`,
            hour.trade_count,
            formatCurrency(hour.total_pnl),
            formatPercent(hour.win_rate),
            hour.winning_trades,
            hour.losing_trades,
          ]);
        }
      });
    }

    timeData.push([], ['Performance by Day of Week']);
    timeData.push(['Day', 'Trades', 'Total P&L', 'Win Rate', 'Wins', 'Losses']);

    // Add day of week performance
    if (data.timeAnalysis.by_day_of_week) {
      const dayMap: { [key: number]: string } = {
        0: 'Monday',
        1: 'Tuesday',
        2: 'Wednesday',
        3: 'Thursday',
        4: 'Friday',
        5: 'Saturday',
        6: 'Sunday',
      };
      data.timeAnalysis.by_day_of_week.forEach((day) => {
        if (day.trade_count > 0) {
          timeData.push([
            dayMap[day.day_index] || day.day || `Day ${day.day_index}`,
            day.trade_count,
            formatCurrency(day.total_pnl),
            formatPercent(day.win_rate),
            day.winning_trades,
            day.losing_trades,
          ]);
        }
      });
    }

    timeData.push([], ['Performance by Month']);
    timeData.push(['Month', 'Trades', 'Total P&L', 'Win Rate', 'Wins', 'Losses']);

    // Add monthly performance
    if (data.timeAnalysis.by_month) {
      data.timeAnalysis.by_month.forEach((month) => {
        if (month.trade_count > 0) {
          const [year, monthNum] = month.month.split('-');
          const date = new Date(parseInt(year), parseInt(monthNum) - 1);
          const monthName = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
          timeData.push([
            monthName,
            month.trade_count,
            formatCurrency(month.total_pnl),
            formatPercent(month.win_rate),
            month.winning_trades,
            month.losing_trades,
          ]);
        }
      });
    }

    const timeSheet = XLSX.utils.aoa_to_sheet(timeData);
    autoSizeColumns(timeSheet);
    XLSX.utils.book_append_sheet(workbook, timeSheet, 'Time Analysis');
  }

  // 5. Symbol Performance Sheet
  if (data.symbolStats?.symbols) {
    const symbolData: any[][] = [
      ['Symbol Performance'],
      [],
      ['Symbol', 'Trades', 'Buy Trades', 'Sell Trades', 'Win Rate', 'Total P&L', 'Avg P&L', 'Avg Win', 'Avg Loss', 'Largest Win', 'Largest Loss', 'Total Return %'],
    ];

    data.symbolStats.symbols.forEach((symbol) => {
      symbolData.push([
        symbol.symbol,
        symbol.total_trades,
        symbol.buy_trades || 0,
        symbol.sell_trades || 0,
        formatPercent(symbol.win_rate),
        formatCurrency(symbol.total_pnl),
        formatCurrency(symbol.average_pnl),
        formatCurrency(symbol.average_win),
        formatCurrency(symbol.average_loss),
        formatCurrency(symbol.best_trade),
        formatCurrency(symbol.worst_trade),
        formatPercent(symbol.total_return_percent),
      ]);
    });

    const symbolSheet = XLSX.utils.aoa_to_sheet(symbolData);
    autoSizeColumns(symbolSheet);
    XLSX.utils.book_append_sheet(workbook, symbolSheet, 'Symbol Performance');
  }

  // 6. Session Performance Sheet
  if (data.sessionPerformance?.sessions && data.sessionPerformance.sessions.length > 0) {
    const sessionData: any[][] = [
      ['Session Performance'],
      [],
      ['Summary'],
      ['Total Trades', data.sessionPerformance.summary.total_trades],
      ['Total P&L', formatCurrency(data.sessionPerformance.summary.total_pnl)],
      ['Total Winning Trades', data.sessionPerformance.summary.total_winning_trades],
      ['Total Losing Trades', data.sessionPerformance.summary.total_losing_trades],
      ['Win Rate', formatPercent(data.sessionPerformance.summary.win_rate)],
      ['Average P&L', formatCurrency(data.sessionPerformance.summary.average_pnl)],
      ['Best Session', data.sessionPerformance.summary.best_session || 'N/A'],
      ['Worst Session', data.sessionPerformance.summary.worst_session || 'N/A'],
      [],
      ['Session Details'],
      ['Session', 'Trades', 'Winning Trades', 'Losing Trades', 'Win Rate %', 'Total P&L', 'Average P&L', 'Largest Win', 'Largest Loss', 'Total Volume', 'Average Volume', 'Avg Duration (min)', 'Profit Factor'],
    ];

    data.sessionPerformance.sessions.forEach((session) => {
      sessionData.push([
        session.session_name,
        session.trade_count,
        session.winning_trades,
        session.losing_trades,
        session.win_rate,
        formatCurrency(session.total_pnl),
        formatCurrency(session.average_pnl),
        session.largest_win > 0 ? formatCurrency(session.largest_win) : 'N/A',
        session.largest_loss < 0 ? formatCurrency(session.largest_loss) : 'N/A',
        formatNumber(session.total_volume, 2),
        formatNumber(session.average_volume, 2),
        formatNumber(session.average_trade_duration_minutes, 1),
        session.profit_factor !== null ? formatNumber(session.profit_factor, 2) : 'N/A',
      ]);
    });

    const sessionSheet = XLSX.utils.aoa_to_sheet(sessionData);
    autoSizeColumns(sessionSheet);
    XLSX.utils.book_append_sheet(workbook, sessionSheet, 'Session Performance');
  }

  // Comparison period sheets (when comparison range is selected)
  if (hasComparison && comp) {
    const compDetailedData: any[][] = [
      ['Detailed Statistics – Comparison Period'],
      compDate?.startDate && compDate?.endDate
        ? ['Period', `${compDate.startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${compDate.endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`]
        : [],
      [],
      ['Metric', 'Value'],
      ['Total Gain/Loss', formatCurrency(comp.total_pnl)],
      ['Average Daily Gain/Loss', formatCurrency(comp.average_daily_gain_loss)],
      ['Average Trade Gain/Loss', formatCurrency(comp.average_per_trade_pnl)],
      ['Total Number of Trades', comp.total_trades],
      ['Number of Scratch Trades', comp.closed_trades - comp.winning_trades - comp.losing_trades],
      ['Trade P&L Standard Deviation', formatCurrency(comp.trade_pnl_standard_deviation)],
      ['Kelly Percentage', formatPercent(comp.kelly_percentage)],
      ['Largest Gain', formatCurrency(comp.largest_win)],
      ['Average Daily Volume', formatNumber(comp.average_daily_volume, 2)],
      ['Average Winning Trade', formatCurrency(comp.average_win)],
      ['Number of Winning Trades', comp.winning_trades],
      ['Max Consecutive Wins', comp.longest_win_streak],
      ['System Quality Number (SQN)', formatNumber(comp.system_quality_number, 2)],
      ['Total Fees', formatCurrency(comp.total_fees)],
      ['Largest Loss', formatCurrency(comp.largest_loss)],
      ['Average Losing Trade', formatCurrency(comp.average_loss)],
      ['Number of Losing Trades', comp.losing_trades],
      ['Max Consecutive Losses', comp.longest_loss_streak],
      ['Profit Factor', formatNumber(comp.profit_factor, 2)],
      ['Total Commissions', formatCurrency(comp.total_commissions)],
    ];
    const compDetailedSheet = XLSX.utils.aoa_to_sheet(compDetailedData);
    autoSizeColumns(compDetailedSheet);
    XLSX.utils.book_append_sheet(workbook, compDetailedSheet, 'Comparison - Detailed');

    const compSystemQualityData: any[][] = [
      ['System Quality – Comparison Period'],
      compDate?.startDate && compDate?.endDate
        ? ['Period', `${compDate.startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${compDate.endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`]
        : [],
      [],
      ['Metric', 'Value'],
      ['Profit Factor', formatNumber(comp.profit_factor, 2)],
      ['Expectancy', formatCurrency(comp.average_per_trade_pnl)],
      ['Kelly Percentage', formatPercent(comp.kelly_percentage)],
      ['SQN', formatNumber(comp.system_quality_number, 2)],
      ['P&L Std Deviation', formatCurrency(comp.trade_pnl_standard_deviation)],
    ];
    const compSystemQualitySheet = XLSX.utils.aoa_to_sheet(compSystemQualityData);
    autoSizeColumns(compSystemQualitySheet);
    XLSX.utils.book_append_sheet(workbook, compSystemQualitySheet, 'Comparison - System Quality');

    const compAveragesReturnsData: any[][] = [
      ['Averages & Returns – Comparison Period'],
      compDate?.startDate && compDate?.endDate
        ? ['Period', `${compDate.startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${compDate.endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`]
        : [],
      [],
      ['Metric', 'Value'],
      ['Average Win', formatCurrency(comp.average_win)],
      ['Average Loss', formatCurrency(comp.average_loss)],
      ['Avg Daily Gain/Loss', formatCurrency(comp.average_daily_gain_loss)],
      ['Avg Per-share Gain/Loss', formatCurrency(comp.average_per_unit_pnl)],
      ['Avg Daily Volume', formatNumber(comp.average_daily_volume, 2)],
    ];
    const compAveragesReturnsSheet = XLSX.utils.aoa_to_sheet(compAveragesReturnsData);
    autoSizeColumns(compAveragesReturnsSheet);
    XLSX.utils.book_append_sheet(workbook, compAveragesReturnsSheet, 'Comparison - Averages');

    const compTimingCostsData: any[][] = [
      ['Timing & Costs – Comparison Period'],
      compDate?.startDate && compDate?.endDate
        ? ['Period', `${compDate.startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${compDate.endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`]
        : [],
      [],
      ['Metric', 'Value'],
      ['Hold Time (Winners)', formatHours(comp.average_hold_time_winning)],
      ['Hold Time (Losers)', formatHours(comp.average_hold_time_losing)],
      ['Hold Time (Scratch)', formatHours(comp.average_hold_time_scratch)],
      ['Total Fees', formatCurrency(comp.total_fees)],
      ['Total Commissions', formatCurrency(comp.total_commissions)],
    ];
    const compTimingCostsSheet = XLSX.utils.aoa_to_sheet(compTimingCostsData);
    autoSizeColumns(compTimingCostsSheet);
    XLSX.utils.book_append_sheet(workbook, compTimingCostsSheet, 'Comparison - Timing');
  }

  if (hasComparison && data.comparisonRiskMetrics) {
    const rm = data.comparisonRiskMetrics;
    const riskCompData: any[][] = [
      ['Risk Metrics – Comparison Period'],
      [],
      ['Metric', 'Value', 'Description'],
      ['Maximum Drawdown', formatCurrency(rm.max_drawdown), 'The largest peak-to-trough decline in portfolio value'],
      ['Max Drawdown %', formatPercent(rm.max_drawdown_percent), 'Maximum drawdown as a percentage of peak value'],
      ['Sharpe Ratio', formatNumber(rm.sharpe_ratio, 2), 'Risk-adjusted return metric'],
      ['Sortino Ratio', formatNumber(rm.sortino_ratio, 2), 'Similar to Sharpe but only penalizes downside volatility'],
      ['Risk/Reward Ratio', rm.risk_reward_ratio ? `${formatNumber(rm.risk_reward_ratio, 2)}:1` : 'N/A', 'Average win divided by average loss'],
      ['Calmar Ratio', formatNumber(rm.calmar_ratio, 2), 'Annual return divided by maximum drawdown'],
      ['Largest Losing Streak', rm.largest_losing_streak, 'Longest consecutive sequence of losing trades'],
      ['Average Losing Trade', formatCurrency(rm.average_losing_trade), 'Average loss per losing trade'],
      ['Volatility', formatPercent(rm.volatility), 'Standard deviation of returns'],
      [],
      ['Drawdown Over Time'],
      ['Date', 'Drawdown'],
    ];
    if (rm.drawdown_data) {
      rm.drawdown_data.forEach((point: { date: string; drawdown: number }) => {
        riskCompData.push([point.date, formatCurrency(point.drawdown)]);
      });
    }
    const riskCompSheet = XLSX.utils.aoa_to_sheet(riskCompData);
    autoSizeColumns(riskCompSheet);
    XLSX.utils.book_append_sheet(workbook, riskCompSheet, 'Comparison - Risk');
  }

  if (hasComparison && data.comparisonTimeAnalysis) {
    const ta = data.comparisonTimeAnalysis;
    const timeCompData: any[][] = [
      ['Time Analysis – Comparison Period'],
      [],
      ['Performance by Hour of Day'],
      ['Hour', 'Trades', 'Total P&L', 'Win Rate', 'Wins', 'Losses'],
    ];
    if (ta.by_hour) {
      ta.by_hour.forEach((hour: { hour: number; trade_count: number; total_pnl: number; win_rate: number; winning_trades: number; losing_trades: number }) => {
        if (hour.trade_count > 0) {
          timeCompData.push([
            `${hour.hour}:00`,
            hour.trade_count,
            formatCurrency(hour.total_pnl),
            formatPercent(hour.win_rate),
            hour.winning_trades,
            hour.losing_trades,
          ]);
        }
      });
    }
    timeCompData.push([], ['Performance by Day of Week']);
    timeCompData.push(['Day', 'Trades', 'Total P&L', 'Win Rate', 'Wins', 'Losses']);
    if (ta.by_day_of_week) {
      const dayMap: { [key: number]: string } = {
        0: 'Monday',
        1: 'Tuesday',
        2: 'Wednesday',
        3: 'Thursday',
        4: 'Friday',
        5: 'Saturday',
        6: 'Sunday',
      };
      ta.by_day_of_week.forEach((day: { day_index: number; day?: string; trade_count: number; total_pnl: number; win_rate: number; winning_trades: number; losing_trades: number }) => {
        if (day.trade_count > 0) {
          timeCompData.push([
            dayMap[day.day_index] || day.day || `Day ${day.day_index}`,
            day.trade_count,
            formatCurrency(day.total_pnl),
            formatPercent(day.win_rate),
            day.winning_trades,
            day.losing_trades,
          ]);
        }
      });
    }
    timeCompData.push([], ['Performance by Month']);
    timeCompData.push(['Month', 'Trades', 'Total P&L', 'Win Rate', 'Wins', 'Losses']);
    if (ta.by_month) {
      ta.by_month.forEach((month: { month: string; trade_count: number; total_pnl: number; win_rate: number; winning_trades: number; losing_trades: number }) => {
        if (month.trade_count > 0) {
          const [year, monthNum] = month.month.split('-');
          const date = new Date(parseInt(year), parseInt(monthNum) - 1);
          const monthName = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
          timeCompData.push([
            monthName,
            month.trade_count,
            formatCurrency(month.total_pnl),
            formatPercent(month.win_rate),
            month.winning_trades,
            month.losing_trades,
          ]);
        }
      });
    }
    const timeCompSheet = XLSX.utils.aoa_to_sheet(timeCompData);
    autoSizeColumns(timeCompSheet);
    XLSX.utils.book_append_sheet(workbook, timeCompSheet, 'Comparison - Time');
  }

  if (hasComparison && data.comparisonSymbolStats?.symbols && data.comparisonSymbolStats.symbols.length > 0) {
    const symbolCompData: any[][] = [
      ['Symbol Performance – Comparison Period'],
      [],
      ['Symbol', 'Trades', 'Buy Trades', 'Sell Trades', 'Win Rate', 'Total P&L', 'Avg P&L', 'Avg Win', 'Avg Loss', 'Largest Win', 'Largest Loss', 'Total Return %'],
    ];
    data.comparisonSymbolStats.symbols.forEach((symbol) => {
      symbolCompData.push([
        symbol.symbol,
        symbol.total_trades,
        symbol.buy_trades ?? 0,
        symbol.sell_trades ?? 0,
        formatPercent(symbol.win_rate),
        formatCurrency(symbol.total_pnl),
        formatCurrency(symbol.average_pnl),
        formatCurrency(symbol.average_win),
        formatCurrency(symbol.average_loss),
        formatCurrency(symbol.best_trade),
        formatCurrency(symbol.worst_trade),
        formatPercent(symbol.total_return_percent),
      ]);
    });
    const symbolCompSheet = XLSX.utils.aoa_to_sheet(symbolCompData);
    autoSizeColumns(symbolCompSheet);
    XLSX.utils.book_append_sheet(workbook, symbolCompSheet, 'Comparison - Symbols');
  }

  if (hasComparison && data.comparisonSessionPerformance?.sessions && data.comparisonSessionPerformance.sessions.length > 0) {
    const sp = data.comparisonSessionPerformance;
    const sessionCompData: any[][] = [
      ['Session Performance – Comparison Period'],
      [],
      ['Summary'],
      ['Total Trades', sp.summary.total_trades],
      ['Total P&L', formatCurrency(sp.summary.total_pnl)],
      ['Win Rate', formatPercent(sp.summary.win_rate)],
      ['Best Session', sp.summary.best_session || 'N/A'],
      ['Worst Session', sp.summary.worst_session || 'N/A'],
      [],
      ['Session Details'],
      ['Session', 'Trades', 'Winning Trades', 'Losing Trades', 'Win Rate %', 'Total P&L', 'Average P&L', 'Largest Win', 'Largest Loss', 'Profit Factor'],
    ];
    sp.sessions.forEach((session: { session_name: string; trade_count: number; winning_trades: number; losing_trades: number; win_rate: number; total_pnl: number; average_pnl: number; largest_win: number; largest_loss: number; profit_factor: number | null }) => {
      sessionCompData.push([
        session.session_name,
        session.trade_count,
        session.winning_trades,
        session.losing_trades,
        session.win_rate,
        formatCurrency(session.total_pnl),
        formatCurrency(session.average_pnl),
        session.largest_win > 0 ? formatCurrency(session.largest_win) : 'N/A',
        session.largest_loss < 0 ? formatCurrency(session.largest_loss) : 'N/A',
        session.profit_factor !== null ? formatNumber(session.profit_factor, 2) : 'N/A',
      ]);
    });
    const sessionCompSheet = XLSX.utils.aoa_to_sheet(sessionCompData);
    autoSizeColumns(sessionCompSheet);
    XLSX.utils.book_append_sheet(workbook, sessionCompSheet, 'Comparison - Session');
  }

  // Write the workbook and trigger download
  XLSX.writeFile(workbook, filename);
};
